import { useCallback } from 'react';
import { useTranslation } from '../i18n/provider';
import { normalizeItemTraitKey, splitItemTraits } from '../utils/itemTraitUtils';

/**
 * Хук для локализации PF2e controlled vocabulary
 * (traits, rarity, sizes, skills, character stats).
 * Runtime payload собирается backend-ом из pf2e.vocabulary_entries,
 * а не из frontend/public/locales/pf2e/*.json.
 *
 * Использование:
 *   const { tRarity, tTrait, tAncestry } = usePF2eTranslation();
 *   tRarity('common')     → "Обычный" (на русской локали)
 *   tSize('Medium')       → "Средний"
 *   tTrait('human')       → "Человек"
 *   tAncestry('dwarf')    → "Дварф"
 */
const SKILL_I18N_KEYS: Record<string, string> = {
  acrobatics: 'acrobatics',
  arcana: 'arcana',
  athletics: 'athletics',
  crafting: 'crafting',
  deception: 'deception',
  diplomacy: 'diplomacy',
  intimidation: 'intimidation',
  lore: 'lore',
  medicine: 'medicine',
  nature: 'nature',
  occultism: 'occultism',
  performance: 'performance',
  religion: 'religion',
  society: 'society',
  stealth: 'stealth',
  survival: 'survival',
  thievery: 'thievery',
};

const SAVE_I18N_KEYS: Record<string, string> = {
  fortitude: 'fortitude',
  reflex: 'reflex',
  will: 'will',
};

const PROFICIENCY_I18N_KEYS: Record<string, string> = {
  untrained: 'untrained',
  trained: 'trained',
  expert: 'expert',
  master: 'master',
  legendary: 'legendary',
};

const SKILL_TERM_PATTERN = /\b(Acrobatics|Arcana|Athletics|Crafting|Deception|Diplomacy|Intimidation|Lore|Medicine|Nature|Occultism|Performance|Religion|Society|Stealth|Survival|Thievery)\b/g;
const SAVE_TERM_PATTERN = /\b(Fortitude|Reflex|Will)\b/g;
const PROFICIENCY_TERM_PATTERN = /\b(Untrained|Trained|Expert|Master|Legendary)\b/g;

function normalizeTermKey(value: string): string {
  return value.trim().toLowerCase().replace(/[\s_-]+/g, '');
}

function toVocabularyKey(value: string): string {
  const words = value.match(/[A-Za-z0-9]+/g) || [];
  if (!words.length) return '';
  const [first, ...rest] = words;
  return (first || '').toLowerCase() + rest.map(word => (
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  )).join('');
}

export function usePF2eTranslation() {
  const t = useTranslation();

  const tRarity = useCallback((rarity: string | null | undefined): string => {
    if (!rarity) return '';
    return t(`pf2e.rarity.${rarity.toLowerCase()}`, {}, rarity);
  }, [t]);

  const tTrait = useCallback((trait: string | null | undefined): string => {
    if (!trait) return '';
    return t(`pf2e.traits.${trait.toLowerCase()}`, {}, trait);
  }, [t]);

  const tItemTrait = useCallback((trait: string | null | undefined): string => {
    if (!trait) return '';
    const normalized = normalizeItemTraitKey(trait);
    const canonicalKey = String(trait).trim().toLowerCase();
    const canonicalItemTrait = canonicalKey
      ? t(`pf2e.items_traits.${canonicalKey}`, {}, '')
      : '';
    const itemTrait = normalized && normalized !== canonicalKey
      ? t(`pf2e.items_traits.${normalized}`, {}, '')
      : '';
    return canonicalItemTrait || itemTrait || tTrait(trait);
  }, [t, tTrait]);

  const tSize = useCallback((size: string | null | undefined): string => {
    if (!size) return '';
    return t(`pf2e.sizes.${size.toLowerCase()}`, {}, size);
  }, [t]);

  const tLanguage = useCallback((language: string | null | undefined): string => {
    if (!language) return '';
    const key = toVocabularyKey(language);
    return key ? t(`pf2e.languages.${key}`, {}, language) : language;
  }, [t]);

  const tVision = useCallback((vision: string | null | undefined): string => {
    if (!vision) return '';
    const key = toVocabularyKey(vision);
    return key ? t(`pf2e.visions.${key}`, {}, vision) : vision;
  }, [t]);

  const translateLanguageText = useCallback((text: string | null | undefined): string => {
    if (!text) return '';
    return text
      .split(',')
      .map(part => part.trim())
      .filter(Boolean)
      .map(tLanguage)
      .join(', ');
  }, [tLanguage]);

  const translateVisionText = useCallback((text: string | null | undefined): string => {
    if (!text) return '';
    return text
      .split(',')
      .map(part => tVision(part.trim()))
      .filter(Boolean)
      .join(', ');
  }, [tVision]);

  const translateSizeText = useCallback((text: string | null | undefined): string => {
    if (!text) return '';
    return text.replace(/\b(Tiny|Small|Medium|Large|Huge|Gargantuan)\b/g, (size) => tSize(size));
  }, [tSize]);

  const parseTranslatedSizes = useCallback((sizeString: string | null | undefined): string[] => {
    if (!sizeString) return [];
    return sizeString
      .split(',')
      .map(size => size.trim())
      .filter(Boolean)
      .map(tSize);
  }, [tSize]);

  const tAncestry = useCallback((ancestry: string | null | undefined): string => {
    if (!ancestry) return '';
    return t(`pf2e.ancestry.${ancestry.toLowerCase()}`, {}, ancestry);
  }, [t]);

  const tSkill = useCallback((skill: string | null | undefined): string => {
    if (!skill) return '';
    const key = SKILL_I18N_KEYS[normalizeTermKey(skill)];
    return key ? t(`pf2e.skills.${key}`, {}, skill) : skill;
  }, [t]);

  const tSave = useCallback((save: string | null | undefined): string => {
    if (!save) return '';
    const key = SAVE_I18N_KEYS[normalizeTermKey(save)];
    return key ? t(`pf2e.saves.${key}`, {}, save) : save;
  }, [t]);

  const tProficiency = useCallback((rank: string | null | undefined): string => {
    if (!rank) return '';
    const key = PROFICIENCY_I18N_KEYS[normalizeTermKey(rank)];
    return key ? t(`pf2e.proficiency.${key}`, {}, rank) : rank;
  }, [t]);

  const translateSkillText = useCallback((text: string | null | undefined): string => {
    if (!text) return '';
    return text.replace(SKILL_TERM_PATTERN, (skill) => tSkill(skill));
  }, [tSkill]);

  const translateSaveText = useCallback((text: string | null | undefined): string => {
    if (!text) return '';
    return text.replace(SAVE_TERM_PATTERN, (save) => tSave(save));
  }, [tSave]);

  const translateProficiencyText = useCallback((text: string | null | undefined): string => {
    if (!text) return '';
    return text.replace(PROFICIENCY_TERM_PATTERN, (rank) => tProficiency(rank));
  }, [tProficiency]);

  /**
   * Парсит строку трейтов и переводит каждый через tTrait().
   * Автоматически фильтрует дубликаты редкости (common/uncommon/rare/unique).
   */
  const parseTranslatedTraits = useCallback((traitString: string | null | undefined): string[] => {
    if (!traitString) return [];
    const RARITY_TRAIT_DUPLICATES = ['common', 'uncommon', 'rare', 'unique'];
    return traitString
      .split(',')
      .map(t => t.trim())
      .filter(Boolean)
      .filter(t => !RARITY_TRAIT_DUPLICATES.includes(t.toLowerCase()))
      .map(tTrait);
  }, [tTrait]);

  const parseTranslatedItemTraits = useCallback((traitString: string | null | undefined): string[] => {
    return splitItemTraits(traitString).map(tItemTrait).filter(Boolean);
  }, [tItemTrait]);

  return {
    tRarity,
    tTrait,
    tItemTrait,
    tSize,
    tLanguage,
    tVision,
    tAncestry,
    tSkill,
    tSave,
    tProficiency,
    translateSizeText,
    translateLanguageText,
    translateVisionText,
    translateSkillText,
    translateSaveText,
    translateProficiencyText,
    parseTranslatedSizes,
    parseTranslatedTraits,
    parseTranslatedItemTraits,
  };
}
